import React from 'react';
import moment from 'moment';
import dynamic from 'next/dynamic';
import { constants } from '@clubmed/utils';
import type { TravelAgency } from 'Components/lines/prebooking-form-line/PrebookingFormLine.utils';
import {
  getNewsletterOptins,
  getTravelAgencyOptions,
} from 'Components/lines/prebooking-form-line/PrebookingFormLine.utils';
import type {
  FormProduct,
  FormStatus,
} from 'Components/widgets/product-quotation-form/productQuotationForm.utils';
import 'Components/widgets/product-quotation-form/productQuotationForm.utils';
import {
  QUOTATION_FORM_SECTION_ID,
  MAX_FORM_ADULTS,
  MAX_FORM_KIDS,
  MAX_FORM_TEENS,
  MIN_FORM_ADULTS,
  MIN_FORM_KIDS,
  MIN_FORM_TEENS,
  NO_VALUE,
  RANGE_ESCAPADE,
} from 'Components/widgets/product-quotation-form/productQuotationForm.constants';
import { addTranslationKeys, t } from 'Src/i18n';
import { getAdultsOptions, getChildrenOptions } from 'Store/utils/selectOptionsBuilders';
import { momentFromApiDate } from 'Utils/date/apiDate';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { useAppContext } from 'Context/ReactAppContext';
import { Title } from 'Components/frameworks/title/Title';
import { CustomerLogin } from 'Components/widgets/prebooking-form/CustomerLogin';

// No SSR — the form renders differently for logged-in users (pre-filled from Redux)
// and always needs client-side Formik validation. Skipping SSR avoids the
// hydration mismatch entirely at no SEO cost (lead forms aren't indexed content).
const ProductQuotationFormContainer = dynamic(
  () =>
    import('Components/widgets/product-quotation-form/ProductQuotationFormContainer').then(
      (m) => m.ProductQuotationFormContainer,
    ),
  { ssr: false },
);
const { PRODUCT_TYPES } = constants;
const PRODUCT_TYPES_WITH_SET_DATES = [
  PRODUCT_TYPES.CRUISE,
  PRODUCT_TYPES.SUMMER_CAMP,
  PRODUCT_TYPES.TOUR,
];
const SUMMER_CAMP_AVAILABLE_DATES = [
  {
    value: '20220702',
  },
  {
    value: '20220709',
  },
  {
    value: '20220716',
  },
];
export type ProductQuotationFormLineProps = {
  coordinates: {
    civility: string;
    email: string;
    firstName: string;
    informationLabel: string;
    lastName: string;
    phoneNumber: string;
    title: string;
    favoriteTravelAgencyLabel: string;
    favoriteTravelAgencyPlaceholder: string;
    gmNumberLabel: string;
    gmNumberPlaceholder: string;
  };
  criteria: {
    dateFormat: string;
    dateLabel: string;
    datePlaceholder: string;
    departureDateEnd: string;
    departureDateStart: string;
    extensionLabel: string;
    extensionPlaceholder: string;
    noExtension: string;
    noTransportation: string;
    title: string;
    transportationDisabled: boolean;
    transportationLabel: string;
    transportationPlaceholder: string;
  };
  login: {
    displayLoginIcon: boolean;
    labelCTA: string;
    memberSpace: string;
    memberSpaceTitle: string;
  };
  pax: {
    adultAgeIndication: string;
    adultsChoices: PluralizedLabel;
    kidsAgeIndicationEscapade: string;
    kidsAgeIndicationSummerCamp: string;
    kidsAgeIndicationTour: string;
    kidsChoices: PluralizedLabel;
    noMoreAvailableDates: string;
    paxLabel: string;
    teensChoices: PluralizedLabel;
    title: string;
  };
  quotation: {
    buttonLabel: string;
    error: FormStatus;
    indication: Array<string>;
    sending: string;
  };
  resortId: string;
  specialRequest: {
    placeholder: string;
    title: string;
  };
  title: string;
};
export type FormFieldOption = {
  name: string;
  value: string;
};
export type ProductQuotationFormResolverProps = {
  bookingCriteria: {
    departureCities: ReadonlyArray<FormFieldOption>;
  };
  civilities: {
    adult: ReadonlyArray<FormFieldOption>;
  };
  config: {
    account: {
      authenticated: {
        greeting: string;
      };
    };
  };
  legalInformations: {
    consentOptins: Array<LegalInformation>;
  };
  newsletter: {
    id: string;
    description: string;
  };
  product: FormProduct;
  stopover?: {
    tour: {
      duration: string;
    };
    extension: {
      duration: string;
      title: string;
    };
  };
  tour: {
    availableDates: ReadonlyArray<FormFieldOption>;
  };
  travelAgencies: null | Array<TravelAgency>;
  success: null | {
    url: string;
  };
};
type ProductQuotationFormProps = ProductQuotationFormLineProps & ProductQuotationFormResolverProps;
export const ProductQuotationFormLine = (props: ProductQuotationFormProps) => {
  const {
    bookingCriteria,
    civilities,
    coordinates,
    config,
    criteria,
    legalInformations,
    login,
    newsletter,
    pax,
    product,
    quotation,
    specialRequest,
    stopover,
    success,
    title,
    tour,
    travelAgencies,
  } = props;
  addTranslationKeys({
    productQuotationForm: {
      coordinates,
      criteria,
      pax,
      quotation,
      login,
      specialRequest,
      title,
    },
  });
  const { isUserLogged: hasUser, locale } = useAppContext();
  const hasLoginButton = !!config?.account?.authenticated?.greeting;
  const { type: productType } = product;
  const { noExtension, noTransportation, transportationDisabled } = criteria;
  const isEscapade = product.rangeId === RANGE_ESCAPADE;
  const isSummerCamp = productType === PRODUCT_TYPES.SUMMER_CAMP;
  const isProductWithSetDates = PRODUCT_TYPES_WITH_SET_DATES.includes(productType) && !isEscapade;
  const extensionName = stopover?.extension?.title || '';
  const extensionOptions = [];

  // The API returns an empty string when no extension is available
  if (extensionName.length > 0) {
    extensionOptions.push(
      {
        name: noExtension,
        value: NO_VALUE,
      },
      {
        name: extensionName,
        value: extensionName,
      },
    );
  }

  let departureCityOptions = bookingCriteria?.departureCities ?? [];

  if (transportationDisabled) {
    departureCityOptions = [
      {
        name: noTransportation,
        value: NO_VALUE,
      },
    ];
  } else if (
    departureCityOptions.length === 1 &&
    departureCityOptions[0].value === NO_VALUE &&
    !isSummerCamp
  ) {
    // The API returns this array when no transport is available:
    // [{value : "NO", name : "No Transport"}]
    departureCityOptions = [];
  }

  const availableDatesIndication = pax.noMoreAvailableDates;
  const ageIndicationAdults = isSummerCamp
    ? pax.kidsAgeIndicationSummerCamp
    : pax.adultAgeIndication;
  const ageIndicationKids = isEscapade ? pax.kidsAgeIndicationEscapade : pax.kidsAgeIndicationTour;
  const today = new Date();
  const futureDate = new Date(today);
  futureDate.setDate(futureDate.getDate() + 1);
  let departureDateStart = new Date(criteria.departureDateStart);
  departureDateStart = moment(departureDateStart).isBefore(moment(today))
    ? futureDate
    : departureDateStart;
  const departureDateEnd = new Date(criteria.departureDateEnd);
  const adultsOptions = isSummerCamp
    ? getChildrenOptions(pax.teensChoices, MIN_FORM_TEENS, MAX_FORM_TEENS).map(
        ({ label, value }) => ({
          name: label,
          value: value.toString(),
        }),
      )
    : getAdultsOptions(pax.adultsChoices, MIN_FORM_ADULTS, MAX_FORM_ADULTS).map(
        ({ label, value }) => ({
          name: label,
          value: value.toString(),
        }),
      );
  const kidsOptions = getChildrenOptions(pax.kidsChoices, MIN_FORM_KIDS, MAX_FORM_KIDS).map(
    ({ label, value }) => ({
      name: label,
      value: value.toString(),
    }),
  );
  const agencies = getTravelAgencyOptions(travelAgencies, locale);
  const consentOptins = getNewsletterOptins(legalInformations.consentOptins || []);
  const redirectUris = {
    success: success ? success.url : null,
  };
  const loginLabels = {
    memberSpace: t('productQuotationForm.login.memberSpace'),
    memberSpaceTitle: t('productQuotationForm.login.memberSpaceTitle'),
    labelCTA: t('productQuotationForm.login.labelCTA'),
  };
  return (
    <div className="relative isolate z-6 bg-white">
      <SiteContainer
        className="Line"
        data-testid={QUOTATION_FORM_SECTION_ID}
        id={QUOTATION_FORM_SECTION_ID}
      >
        <div className="PriceTicket-sticky-beacon" />
        <Title className="mb-20 mt-30 px-10" hLevel={2} theme="title-sans-n4">
          {t('productQuotationForm.title')}
        </Title>
        {(!login || login.displayLoginIcon) && (
          <CustomerLogin hasLoginButton={hasLoginButton} hasUser={hasUser} labels={loginLabels} />
        )}
        <ProductQuotationFormContainer
          adultsOptions={adultsOptions}
          ageIndicationAdults={ageIndicationAdults}
          ageIndicationKids={ageIndicationKids}
          agencies={agencies}
          availableDatesIndication={availableDatesIndication}
          civilityOptions={civilities?.adult || []}
          dateFormat={criteria.dateFormat}
          departureCityDisabled={transportationDisabled}
          departureCityOptions={departureCityOptions}
          departureDateEnd={departureDateEnd}
          departureDateOptions={
            // TODO: this is done for ODR purpose to do remove this when
            // the summer camp date are set
            isSummerCamp
              ? SUMMER_CAMP_AVAILABLE_DATES.map(({ value }) => ({
                  name: momentFromApiDate(value).format(criteria.dateFormat),
                  value,
                }))
              : (tour?.availableDates ?? []).map(({ name, value }) => ({
                  name,
                  value: value.toString(),
                }))
          }
          departureDateStart={departureDateStart}
          extensionOptions={extensionOptions}
          footnotes={quotation.indication}
          hasUser={hasUser}
          isProductWithSetDates={isProductWithSetDates}
          isSummerCamp={isSummerCamp}
          kidsOptions={kidsOptions}
          newsletter={newsletter}
          consentOptins={consentOptins}
          product={product}
          redirectUris={redirectUris}
          quotation={{
            error: quotation.error,
          }}
        />
      </SiteContainer>
    </div>
  );
};
